<script setup lang="ts">
import { ref, computed } from 'vue';
import { useMainStore } from '../../store';
import { useLeadSubmit } from '../../composables/useLeadSubmit';
import { useGateAnalytics } from '../../composables/useGateAnalytics';
import { trackQuizQuestion } from '../../analytics/events';
import type { FlowScreen, Gate, Question, QuizData } from '../../types';
import type { TemplateDefinition } from '../../templates';

// Multi-question single-page block (DEV-1135, select-form funnels): several
// questions answered inline (dropdowns) + the lead form on one screen. Same
// money path as GateBlock via useLeadSubmit; inline answers are recorded just
// before submit. Template binds each select to `ui.values[question.id]`
// (answer id string; '' = unanswered — templates should mark selects
// required so native validation blocks submit).
const props = defineProps<{
  screen: Extract<FlowScreen, { type: 'form-gate' }>;
  template: TemplateDefinition;
}>();

const store = useMainStore();
const quizData = store.quizData as QuizData;
const gate = (store.gateData || {}) as Gate;
const form = ref(store.formLead);
const consentState = ref({ checked: false });
const ui = ref({ values: {} as Record<string, string> });

const questions = computed(() =>
  props.screen.question_ids
    .map((qid) => store.quizData.questions?.find((q) => q.id === qid))
    .filter((q): q is Question => !!q)
);

useGateAnalytics(quizData, form);

const hasConsent = !!gate.consent_checkbox;
const { submit, skip } = useLeadSubmit(
  quizData,
  form,
  hasConsent ? { consent: computed(() => consentState.value.checked) } : undefined
);

const recordInlineAnswers = () => {
  questions.value.forEach((q, index) => {
    const value = ui.value.values[q.id];
    if (!value) return;
    const answer = q.answers?.find((a) => Array.isArray(a) && a[0] === value);
    if (!answer) return;
    const already = store.questionAnswers.some(
      ([qId, a]) => qId === q.id && a === answer[0]
    );
    if (!already) {
      trackQuizQuestion({
        quiz_id: store.quizData.quiz_id,
        question_index: index,
        question_text: q.title,
        answer_text: String(answer[1]),
      });
    }
    store.recordFlowAnswer([q.id, answer[0]]);
  });
};

const handleSubmit = async () => {
  recordInlineAnswers();
  await submit();
};

const handleSkip = () => {
  recordInlineAnswers();
  skip();
};
</script>

<template>
  <component
    :is="template.formGate"
    v-if="questions.length"
    :questions="questions"
    :gate="gate"
    :quiz="quizData"
    :form="form"
    :ui="ui"
    :consent="consentState"
    :fetching="store.fetching"
    @submit="handleSubmit"
    @skip="handleSkip"
  />
</template>
